"use client";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency, formatMinutes, formatTimeWithPreference } from "@/lib/format";
import { useTimeFormat } from "@/lib/time-format-context";
import { format, parseISO } from "date-fns";
import { Car, Package } from "lucide-react";
import { RatingDisplay } from "./route-rating";

interface BlockWithShift {
  _id: string;
  date: string;
  shiftStart: string;
  shiftEnd: string;
  basePay: number;
  tip: number;
  noRoutePayout: number;
  station?: string;
  manualMiles?: number;
  routeRating?: number;
  overbooked?: boolean;
  stops?: number;
  locations?: number;
  packages?: number;
  shift?: {
    totalMiles: number;
    status: string;
    startedAt?: number;
    endedAt?: number;
    pauseDuration?: number;
  } | null;
}

interface BlockCardProps {
  block: BlockWithShift;
}

export function BlockCard({ block }: BlockCardProps) {
  const timeFormat = useTimeFormat();
  const totalEarnings = block.basePay + block.tip + block.noRoutePayout;
  const formattedDate = format(parseISO(block.date), "EEE, MMM d");

  // Duration
  const [startH, startM] = block.shiftStart.split(":").map(Number);
  const [endH, endM] = block.shiftEnd.split(":").map(Number);
  const durationMins = endH * 60 + endM - (startH * 60 + startM);
  const durationStr = formatMinutes(durationMins);

  // Miles - prefer shift miles if > 0, otherwise fall back to manualMiles
  const shiftMiles = block.shift?.totalMiles;
  const miles = (shiftMiles && shiftMiles > 0) ? shiftMiles : (block.manualMiles ?? null);

  // Status
  const getStatus = () => {
    if (block.shift?.status === "active")
      return { label: "Active", variant: "default" as const };
    if (block.shift?.status === "paused")
      return { label: "Paused", variant: "secondary" as const };
    if (block.shift) return { label: "Tracked", variant: "outline" as const };
    if (block.manualMiles)
      return { label: "Manual", variant: "outline" as const };
    return null;
  };

  const status = getStatus();

  return (
    <Card className="border-0 shadow-none">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium">{formattedDate}</p>
              {block.station && (
                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {block.station}
                </span>
              )}
              {status && (
                <Badge variant={status.variant} className="text-xs">
                  {status.label}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatTimeWithPreference(block.shiftStart, timeFormat)} – {formatTimeWithPreference(block.shiftEnd, timeFormat)} ({durationStr})
            </p>
          </div>
          <div className="text-right">
            <p className="font-semibold text-lg">
              {formatCurrency(totalEarnings)}
            </p>
            {block.overbooked && (
              <p className="text-xs text-amber-600 dark:text-amber-500">Overbooked</p>
            )}
          </div>
        </div>

        {/* Mileage, packages, and rating */}
        {(miles !== null || block.packages || block.routeRating) && (
          <div className="flex items-center gap-3 mt-2 text-sm">
            {miles !== null && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Car className="h-3.5 w-3.5" />
                <span>{miles.toFixed(1)} mi</span>
              </div>
            )}
            {block.packages && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Package className="h-3.5 w-3.5" />
                <span>{block.packages} pkg</span>
              </div>
            )}
            {block.routeRating && (
              <RatingDisplay rating={block.routeRating} size="sm" />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
